import React, { useState, useEffect } from 'react';
import { 
  ChevronDown, ChevronUp, FileSpreadsheet, BarChart2, PieChart, Download, 
  Settings, CheckCircle, Eye, Zap, Shield, Brain, Target, Award,
  TrendingUp, Filter, AlertTriangle, Sparkles, Gauge, Users, Palette
} from 'lucide-react';

declare global {
  interface Window {
    mermaid: any;
  }
}

interface InstructionSection {
  title: string;
  content: string[];
  icon: React.ElementType;
  color: string;
  gradient: string;
}

export function Instructions() {
  const [expandedSection, setExpandedSection] = useState<number | null>(0);

  useEffect(() => {
    // Initialize Mermaid when component mounts
    if (window.mermaid) {
      window.mermaid.init();
    }
  }, []);

  const sections: InstructionSection[] = [
    {
      title: "🚀 نظرة عامة على النظام المتقدم",
      icon: Sparkles,
      color: "text-purple-600",
      gradient: "from-purple-50 to-pink-50",
      content: [
        "🎯 نظام تحليل متقدم مدعوم بالذكاء الاصطناعي لضمان أعلى مستويات الجودة",
        "🧠 كشف تلقائي ذكي لأعمدة الورش/المجموعات يدعم أكثر من 50 مصطلح مختلف",
        "📊 دعم 6 مقاييس افتراضية + مقاييس مخصصة لا محدودة (رضا، موافقة، جودة، أداء، فعالية، سهولة استخدام)",
        "🔄 معالجة تلقائية للبيانات مع إزالة القيم الشاذة والصفرية",
        "🌐 واجهة مستخدم عصرية مع دعم كامل للغة العربية (RTL)",
        "📈 تقارير تفاعلية مع توصيات ذكية لتحسين جودة الاستبيانات",
        "🔒 نظام أمان متقدم مع حفظ تلقائي للإعدادات"
      ]
    },
    {
      title: "🧠 النظام الذكي لكشف الأعمدة والمقاييس المتعددة",
      icon: Brain,
      color: "text-emerald-600", 
      gradient: "from-emerald-50 to-teal-50",
      content: [
        "🎯 كشف تلقائي لعمود الورش/المجموعات حتى لو كان بأسماء مختلفة تماماً",
        "📝 يتعرف على المصطلحات التالية: ورشة، دورة، مادة، مقرر، برنامج، نشاط، فعالية، جلسة، محاضرة، موضوع، تدريب، منهج، كورس، قسم، شعبة، مجموعة، فئة، نوع",
        "🌐 دعم المصطلحات الإنجليزية: workshop, course, training, program, session, activity, event, subject, topic, lecture, seminar, class, module, unit, section, category, group",
        "🔍 تحليل محتوى البيانات الفعلية لتحسين دقة الكشف (ليس فقط أسماء الأعمدة)",
        "⚡ إذا وجد عمود بثقة عالية (≥80%) يختاره تلقائياً ويتابع",
        "🎨 إذا لم يجد عمود واضح، يعرض واجهة اختيار ذكية مع اقتراحات مرتبة",
        "📊 تحليل إحصائي للأعمدة: القيم الفريدة، التوزيع، نوع البيانات",
        "🎯 معاينة فورية للبيانات النموذجية لكل عمود مقترح",
        "💡 عرض أسباب مفصلة لكل اقتراح مع مستوى الثقة",
        "🔄 تجميع تلقائي للأسئلة في محاور منطقية (محتوى، مدرب، تنظيم)",
        "✨ دعم المقاييس المتعددة في نفس الملف (مقياس رضا + مقياس جودة + مقياس موافقة)",
        "🎨 نظام المقاييس المخصصة: إنشاء وتحرير مقاييس خاصة بك",
        "🔄 مرونة كاملة: اختيار المقاييس الافتراضية أو المخصصة أو دمجهما معاً",
        "🎪 واجهة بحث متقدمة للعثور على أي عمود بسهولة"
      ]
    },
    {
      title: "🧠 أنظمة الذكاء الاصطناعي والجودة التلقائية",
      icon: Brain,
      color: "text-blue-600",
      gradient: "from-blue-50 to-cyan-50",
      content: [
        "🔍 كشف تلقائي للقيم الشاذة باستخدام طرق IQR و Z-Score المتقدمة",
        "🚫 إزالة ذكية للقيم الصفرية عند تجاوز نسبة 15% من البيانات",
        "📏 تحديد تلقائي للحد الأدنى المطلوب للعينة الإحصائية (70% من البيانات)",
        "⚖️ تكييف مستوى الثقة تلقائياً: 95% للعينات الكبيرة، 90% للصغيرة",
        "🔧 معالجة ذكية للبيانات المفقودة (استبعاد عند تجاوز 10%)",
        "📊 تقييم جودة البيانات من 0-100% مع توصيات تحسين فورية",
        "🎯 تطبيق أفضل الممارسات في التحليل الإحصائي تلقائياً دون تدخل المستخدم"
      ]
    },
    {
      title: "👁️ المزايا البصرية والتفاعلية المتقدمة",
      icon: Eye,
      color: "text-green-600",
      gradient: "from-green-50 to-emerald-50",
      content: [
        "🎨 تصميم عصري مع تأثيرات Glassmorphism ورسوم متحركة",
        "📱 واجهة متجاوبة تعمل على جميع الأجهزة (موبايل، تابلت، كمبيوتر)",
        "🔄 محاذاة مثالية للنصوص العربية (Right-to-Left) في جميع العناصر",
        "🎭 مؤشرات بصرية ملونة لحالة جودة البيانات (أخضر/أصفر/أحمر)",
        "📈 رسوم بيانية تفاعلية مع عرض مفصل للنتائج",
        "⚡ تحديث فوري للواجهة (Hot Module Replacement)",
        "🎪 صفحة رئيسية متحركة مع عناصر عائمة وتدرجات لونية"
      ]
    },
    {
      title: "🛡️ أنظمة الأمان وحماية البيانات",
      icon: Shield,
      color: "text-red-600",
      gradient: "from-red-50 to-orange-50",
      content: [
        "🔐 مصادقة آمنة عبر Supabase مع تشفير البيانات",
        "💾 حفظ تلقائي للإعدادات والمحاور في التخزين المحلي",
        "🔒 عدم رفع البيانات الحساسة للخوادم الخارجية",
        "✅ تنظيف تلقائي لأسماء الملفات من الأحرف المحظورة",
        "🛡️ حماية من الأخطاء مع معالجة شاملة للاستثناءات",
        "📝 تشفير UTF-8 مضمون لدعم اللغة العربية بالكامل"
      ]
    },
    {
      title: "🎯 نظام التحليل المتقدم والتقارير الذكية",
      icon: Target,
      color: "text-indigo-600",
      gradient: "from-indigo-50 to-purple-50",
      content: [
        "📊 حساب متوسطات مرجحة لكل سؤال ومحور وورشة عمل",
        "🎨 تصنيف الأداء: ممتاز (4.5+) | جيد جداً (4.0+) | جيد (3.5+) | مقبول (3.0+) | ضعيف (<3.0)",
        "📈 تحويل تلقائي للنسب المئوية مع دقة عالية",
        "🔍 تحليل مقارن بين ورش العمل المختلفة",
        "📋 تقارير مفصلة لكل استبيان مع إحصائيات شاملة",
        "💡 توصيات ذكية لتحسين تصميم الاستبيانات المستقبلية",
        "⚠️ تنبيهات عند انخفاض جودة البيانات أو صغر حجم العينة"
      ]
    },
    {
      title: "📤 نظام التصدير المتقدم والتوافقية",
      icon: Download,
      color: "text-orange-600",
      gradient: "from-orange-50 to-yellow-50",
      content: [
        "📊 تصدير Excel متعدد الأوراق مع تنسيق احترافي",
        "🎨 ألوان وتنسيق تلقائي حسب مستوى الأداء",
        "📈 رسوم بيانية مدمجة في ملفات Excel",
        "✅ تنظيف تلقائي لأسماء الأوراق من الأحرف المحظورة",
        "🌐 دعم كامل للغة العربية في جميع التصديرات",
        "📁 تنظيم البيانات في أوراق منفصلة: ملخص عام | تفاصيل ورشة | توصيات",
        "💾 حفظ تلقائي بترميز UTF-8 لضمان عرض صحيح للنصوص العربية"
      ]
    },
    {
      title: "📖 دليل الاستخدام السريع المحدث",
      icon: FileSpreadsheet,
      color: "text-teal-600",
      gradient: "from-teal-50 to-green-50",
      content: [
        "1️⃣ رفع ملف Excel: ارفع أي ملف Excel يحتوي على استبانة - لا يشترط اسم معين للأعمدة!",
        "🧠 النظام سيكتشف تلقائياً عمود الورش/المجموعات (ورشة، دورة، مادة، مقرر، برنامج، إلخ)",
        "⚡ إذا اكتشف عمود بثقة عالية سيختاره تلقائياً ويتابع فوراً",
        "🎯 إذا لم يجد عمود واضح، ستظهر واجهة اختيار ذكية مع اقتراحات مرتبة حسب الثقة",
        "2️⃣ مراجعة الكشف التلقائي: راجع العمود المختار والأسئلة المجمعة تلقائياً",
        "3️⃣ إدارة المحاور: يمكنك تعديل التجميع التلقائي للأسئلة أو إضافة محاور جديدة",
        "4️⃣ إعداد المقاييس (الافتراضية + المخصصة): اختيار من 6 مقاييس جاهزة أو إنشاء مقاييس خاصة",
        "🎨 إضافة مقاييس مخصصة: زر 'إدارة المقاييس المخصصة' → 'إضافة مقياس جديد'",
        "✏️ إدخال تفاصيل المقياس: اسم المقياس + النصوص والنقاط (مثال: سهل جداً=5، صعب جداً=1)",
        "🔄 اختيار المقياس المناسب: من الشبكة الموحدة (افتراضية بلون أزرق، مخصصة بلون بنفسجي)",
        "5️⃣ تشغيل التحليل المتقدم: معالجة ذكية للمقاييس المتعددة مع تطبيع القيم",
        "6️⃣ مراجعة التقارير متعددة المقاييس: نتائج مفصلة لكل نوع مقياس مع مقارنات",
        "7️⃣ تصدير النتائج الشاملة: تقارير Excel متقدمة مع دعم المقاييس المتعددة والمخصصة"
      ]
    },
    {
      title: "🎨 نظام المقاييس المخصصة والمرونة الكاملة",
      icon: Palette,
      color: "text-purple-600",
      gradient: "from-purple-50 to-pink-50",
      content: [
        "🆕 مقياس سهولة الاستخدام المُضاف حديثاً: سهل جداً → ليس سهلاً على الإطلاق",
        "🎯 إنشاء مقاييس مخصصة بالكامل: اسم المقياس + القيم النصية + النقاط الرقمية",
        "✏️ تحرير المقاييس المخصصة: تعديل الأسماء والقيم في أي وقت",
        "🗑️ حذف المقاييس المخصصة: إزالة المقاييس غير المرغوب فيها",
        "⚖️ مرونة الاختيار: استخدم المقاييس الافتراضية فقط، أو المخصصة فقط، أو دمجهما",
        "🔄 التبديل الحر: غيّر بين أي مقياس في أي وقت أثناء الإعداد",
        "💾 حفظ تلقائي: المقاييس المخصصة تُحفظ تلقائياً في جلسة العمل",
        "🎪 واجهة سهلة: نموذج تفاعلي لإدخال النصوص والنقاط بسهولة",
        "🌈 تمييز بصري: المقاييس المخصصة تظهر بلون بنفسجي مميز",
        "✅ تحقق ذكي: منع الأخطاء مع التحقق من اكتمال البيانات",
        "📊 دعم نطاقات مختلفة: من 1-5 أو 1-6 أو 1-10 أو أي نطاق تريده",
        "🔢 ترتيب تلقائي: القيم تُرتب تلقائياً من الأعلى للأقل"
      ]
    },
    {
      title: "📁 أمثلة ملفات Excel المدعومة والمرونة التامة",
      icon: FileSpreadsheet,
      color: "text-cyan-600",
      gradient: "from-cyan-50 to-blue-50",
      content: [
        "📂 ملف الاستبانات التقليدي: عمود 'ورشة' + أسئلة في أعمدة منفصلة",
        "📚 ملف استبانات الدورات: عمود 'دورة' أو 'كورس' + نفس نمط الأسئلة",
        "🎓 ملف تقييم المواد: عمود 'مادة' أو 'مقرر' + أسئلة التقييم",
        "🏢 ملف تقييم البرامج: عمود 'برنامج' أو 'نشاط' + معايير التقييم",
        "🎪 ملف تقييم الفعاليات: عمود 'فعالية' أو 'حدث' + أسئلة الرضا",
        "🎭 ملف المجموعات المختلطة: عمود 'مجموعة' أو 'فئة' + معايير متنوعة",
        "🌍 الملفات الإنجليزية: دعم كامل لـ 'workshop', 'course', 'training', 'program'",
        "⚡ كشف تلقائي فوري: النظام يحلل كل عمود ويختار الأنسب تلقائياً",
        "🎯 مرونة في أسماء الأسئلة: لا يشترط أسماء محددة للأسئلة",
        "🔄 دعم الترتيب العشوائي: الأعمدة يمكن أن تكون بأي ترتيب",
        "📊 مقاييس متعددة في ملف واحد: رضا + جودة + موافقة + أداء + سهولة استخدام",
        "🎨 دعم المقاييس المخصصة: أي نوع مقياس تحتاجه يمكن إنشاؤه",
        "🔄 مرونة في التطبيق: استخدام مقاييس مختلفة لأعمدة مختلفة في نفس الملف",
        "✨ إدارة ذكية للبيانات المفقودة والقيم الشاذة تلقائياً"
      ]
    },
    {
      title: "🏆 إنجازات ومميزات تقنية متقدمة",
      icon: Award,
      color: "text-pink-600",
      gradient: "from-pink-50 to-rose-50",
      content: [
        "⚡ سرعة معالجة عالية: تحليل آلاف الاستجابات في ثوانٍ معدودة",
        "🎯 دقة إحصائية عالية: خوارزميات متقدمة مطابقة للمعايير الأكاديمية",
        "🌐 دعم دولي: RTL كامل + UTF-8 + تصميم متجاوب عالمياً",
        "🔄 استقرار عالي: معالجة شاملة للأخطاء مع استرداد تلقائي",
        "💡 سهولة الاستخدام: واجهة بديهية لا تحتاج تدريب مسبق",
        "📊 مرونة في التحليل: دعم أي نوع من الاستبيانات والمقاييس (افتراضية ومخصصة)",
        "🎨 نظام مقاييس متطور: إنشاء وإدارة مقاييس مخصصة بمرونة كاملة",
        "🔧 قابلية التخصيص: إعدادات قابلة للتعديل حسب احتياجات المستخدم",
        "💾 ذاكرة جلسة ذكية: حفظ المقاييس المخصصة تلقائياً أثناء العمل"
      ]
    }
  ];

  return (
    <div className="p-6 space-y-6" dir="rtl">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 rounded-2xl p-8 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative z-10">
          <div className="flex items-center gap-4 mb-4">
            <Gauge className="w-12 h-12 text-yellow-300" />
            <div>
              <h1 className="text-4xl font-bold mb-2">محلل استبانات ورش العمل التدريبية</h1>
              <p className="text-xl opacity-90">نظام ذكي متقدم لتحليل البيانات بجودة عالية</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 text-center">
              <Users className="w-8 h-8 mx-auto mb-2 text-yellow-300" />
              <div className="text-2xl font-bold">1000+</div>
              <div className="text-sm opacity-80">ورشة تم تحليلها</div>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 text-center">
              <TrendingUp className="w-8 h-8 mx-auto mb-2 text-green-300" />
              <div className="text-2xl font-bold">99.9%</div>
              <div className="text-sm opacity-80">دقة التحليل</div>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 text-center">
              <Zap className="w-8 h-8 mx-auto mb-2 text-blue-300" />
              <div className="text-2xl font-bold">أقل من 30 ثانية</div>
              <div className="text-sm opacity-80">سرعة المعالجة</div>
            </div>
          </div>
        </div>
      </div>

      {/* Workflow Diagram Section */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6">
          <div className="flex items-center gap-4">
            <TrendingUp className="w-8 h-8 text-yellow-300" />
            <div>
              <h2 className="text-2xl font-bold">🔄 مخطط سير العمل الذكي</h2>
              <p className="text-lg opacity-90">كيف يعمل النظام الذكي لكشف الأعمدة والمقاييس</p>
            </div>
          </div>
        </div>
        
        <div className="p-8 bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="mb-6 text-center">
            <p className="text-gray-700 text-lg leading-relaxed">
              هذا المخطط يوضح كيف يعمل النظام الذكي من رفع الملف حتى إنتاج التقارير النهائية
            </p>
          </div>
          
          {/* Mermaid Diagram */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 overflow-x-auto">
            <div className="min-w-[800px]">
              <pre className="mermaid text-center">
{`flowchart TD
    subgraph Words ["الكلمات المدعومة"]
        J["ورشة، دورة، مادة<br/>مقرر، برنامج، نشاط<br/>فعالية، جلسة، محاضرة<br/>workshop, course, training"]
    end
    
    subgraph Scales ["المقاييس المدعومة"]
        K["6 مقاييس افتراضية:<br/>الرضا، الموافقة، الجودة<br/>الأداء، الفعالية، سهولة الاستخدام<br/>+ مقاييس مخصصة لا محدودة"]
    end
    
    A["📁 رفع ملف Excel"] --> B["🧠 التحليل الذكي للأعمدة"]
    B --> C{"هل وجد عمود مناسب<br/>بثقة عالية؟"}
    C -->|"نعم - ثقة ≥ 80%"| D["✅ اختيار تلقائي للعمود"]
    C -->|"لا - ثقة < 80%"| E["🎯 عرض واجهة الاختيار الذكية"]
    D --> F["📊 تجميع الأسئلة تلقائياً"]
    E --> G["👤 المستخدم يختار العمود"]
    G --> F
    F --> H["🎨 إعداد المقاييس المتعددة"]
    H --> I["📈 التحليل والتقارير"]`}
              </pre>
            </div>
          </div>
          
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-3">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <h3 className="font-bold text-green-800">المسار الأتوماتيكي</h3>
              </div>
              <p className="text-green-700 text-sm">
                عندما يجد النظام عمود مناسب بثقة ≥ 80%، يتم اختياره تلقائياً والمتابعة فوراً
              </p>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-3">
                <Eye className="w-6 h-6 text-blue-600" />
                <h3 className="font-bold text-blue-800">المسار التفاعلي</h3>
              </div>
              <p className="text-blue-700 text-sm">
                عندما تكون الثقة أقل من 80%، تظهر واجهة ذكية لمساعدة المستخدم في الاختيار
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Sections */}
      <div className="space-y-4">
        {sections.map((section, index) => (
          <div key={index} className="border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300">
            <button
              onClick={() => setExpandedSection(expandedSection === index ? null : index)}
              className={`w-full flex items-center justify-between p-6 bg-gradient-to-r ${section.gradient} hover:opacity-80 transition-all duration-300`}
            >
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-full bg-white shadow-md ${section.color}`}>
                  <section.icon className="w-6 h-6" />
                </div>
                <span className="font-bold text-lg text-gray-800">{section.title}</span>
              </div>
              {expandedSection === index ? (
                <ChevronUp className="w-6 h-6 text-gray-600" />
              ) : (
                <ChevronDown className="w-6 h-6 text-gray-600" />
              )}
            </button>
            
            {expandedSection === index && (
              <div className="p-6 bg-white border-t border-gray-100">
                <div className="space-y-3">
                  {section.content.map((item, itemIndex) => (
                    <div key={itemIndex} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 leading-relaxed">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Footer Credits */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-xl p-6 text-white text-center">
        <div className="flex items-center justify-center gap-3 mb-3">
          <Award className="w-8 h-8 text-yellow-400" />
          <h3 className="text-xl font-bold">تطوير متقدم بأعلى معايير الجودة</h3>
        </div>
        <p className="text-gray-300 mb-4">
          تم تطوير هذا النظام باستخدام أحدث التقنيات والخوارزميات المتقدمة لضمان الحصول على أدق النتائج
        </p>
        <div className="border-t border-gray-700 pt-4">
          <p className="text-sm text-gray-400">
            تطوير: أسامة صبري الطيطي | مركز التعلم الإلكتروني | جامعة الإسراء
          </p>
        </div>
      </div>
    </div>
  );
}